import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../../../components/ui/Button';
import { toggleDialog } from '../../../../../../redux/Slices/Global/globalSlice';
import { useUpdateCampaignLayerSettings, useUpdateLiveAnimationSettingsMutation } from '../../../../../../queries';
import { ALL_DIRECTIONS, ANIM_TYPES } from '../../../../../../config/campaigns';
import { readImageFit } from '../../../../../../utils/imageFit';

const FITS = ['fit', 'crop', 'fill'];

const NORMALIZE_LAYER = (raw) => {
  const key = String(raw || '').toUpperCase().trim();
  if (key === 'MIAN') return 'MAIN';
  return ['MAIN', 'PLAYGROUND', 'CORNER', 'BANNER'].includes(key) ? key : 'MAIN';
};

const layerToSettingsKey = {
  MAIN: 'media_animation',
  BANNER: 'banner_media_animation',
  CORNER: 'corner_media_animation',
  PLAYGROUND: 'playground_media_animation',
};

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

/** Direction sets allowed per type — mirrors CampaignController.php's own
 * $byTypeAllowed exactly (re-verified against the live backend code, not
 * assumed): FADE/DISSOLVE take no direction (server forces CENTER); FLIP is
 * HORIZONTAL/VERTICAL; SLIDE/ZOOM/SCROLL/ROLL are LEFT/RIGHT/UP/DOWN
 * (ZOOM additionally allows CENTER). */
const directionsFor = (type) => ALL_DIRECTIONS[type] || [];

const sanitizeAnim = (raw) => {
  const type = raw?.type ?? 'FADE';
  const enabled = !!raw?.enabled;
  const duration = Number.isFinite(raw?.duration) ? clamp(raw.duration, 0.2, 2.0) : 0.8;
  const allowed = directionsFor(type);
  let direction = (type === 'FADE' || type === 'DISSOLVE')
    ? 'CENTER'
    : (raw?.direction && allowed.includes(raw.direction) ? raw.direction : (type === 'FLIP' ? 'HORIZONTAL' : 'LEFT'));
  return { enabled, type, duration, direction };
};

/**
 * Builder-only layer settings sheet — .claude/pp-campaign-builder-prototype.html's
 * openLayerSheet(). Two tabs: "العرض" (apply_layer_settings + media/overlay
 * flags, PATCH /campaign/update/{id}/layer) and "الانتقال" (per-layer
 * transition, PATCH /campaign/{id}/update/animation) — same save hooks the
 * existing LayerSettings.jsx/MediaAnimationForm.jsx already use. Those two
 * components are NOT modified — still used verbatim by nothing else today,
 * but left completely untouched per the zero-diff guardrail.
 *
 * `layer` and `tab` come from redux (`state.global.layer` /
 * `layerSettingsTab`), set explicitly by the caller (CampaignContentSetup.jsx
 * for المحتوى الرئيسي, CampaignVibesSection.jsx for الصور (أجواء)) before
 * opening — never inferred, so this sheet never guesses which layer it's for.
 */
function BuilderLayerSettingsSheet({ layerData = [] }) {
  const { t } = useTranslation();
  const { id: campaignId } = useParams();
  const dispatch = useDispatch();
  const { openDialog, campaignDetails, layer: rawLayer, layerSettingsTab } = useSelector((s) => s.global);
  const layer = useMemo(() => NORMALIZE_LAYER(rawLayer), [rawLayer]);

  const [show, setShow] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setShow(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const [tab, setTab] = useState(layerSettingsTab === 'anim' ? 'anim' : 'display');

  const stored = campaignDetails?.settings?.live?.layer_settings?.[layer] || {};
  const [d, setD] = useState(() => ({
    apply_layer_settings: !!stored.apply_layer_settings,
    is_muted: !!stored.is_muted,
    image_fit: readImageFit({ settings: stored }) || (stored.maintain_aspect_ratio === false ? 'fill' : 'crop'),
    show_layer_one: !!stored.show_layer_one,
    show_layer_two: !!stored.show_layer_two,
    show_layer_three: !!stored.show_layer_three,
    settings: {
      width: stored?.settings?.width ?? 100,
      height: stored?.settings?.height ?? 100,
      start_margin: stored?.settings?.start_margin ?? 0,
      top_margin: stored?.settings?.top_margin ?? 0,
    },
  }));

  const animKey = layerToSettingsKey[layer];
  const [anim, setAnim] = useState(() => sanitizeAnim(campaignDetails?.settings?.live?.[animKey]));

  const updateLayerSettings = useUpdateCampaignLayerSettings();
  const animMutation = useUpdateLiveAnimationSettingsMutation();

  const close = () => {
    setShow(false);
    setTimeout(() => dispatch(toggleDialog('layerSettings')), 260);
  };

  const saveDisplay = () => {
    updateLayerSettings.mutate({
      id: campaignId,
      layer,
      layer_settings: {
        apply_layer_settings: d.apply_layer_settings,
        is_muted: d.is_muted ? 1 : 0,
        image_fit: d.image_fit,
        ...(layer === 'MAIN' ? {
          show_layer_one: d.show_layer_one ? 1 : 0,
          show_layer_two: d.show_layer_two ? 1 : 0,
          show_layer_three: d.show_layer_three ? 1 : 0,
        } : {}),
        settings: d.settings,
      },
    });
  };

  const saveAnim = async () => {
    const payload = { all_layer: false, [animKey]: {
      enabled: anim.enabled,
      type: anim.type,
      duration: Number(clamp(anim.duration, 0.2, 2.0)),
      direction: (anim.type === 'FADE' || anim.type === 'DISSOLVE') ? 'CENTER' : anim.direction,
    } };
    await animMutation.mutateAsync({ id: campaignId, data: payload });
  };

  const hasVideo = layer === 'MAIN' && (layerData || []).some((x) => String(x.type).toUpperCase() === 'VIDEO');
  const title = layer === 'MAIN' ? t('show.show_layer_sheet_title_main') : t('show.show_layer_sheet_title_vibes');
  const dirOptions = (anim.type === 'FADE' || anim.type === 'DISSOLVE') ? [] : directionsFor(anim.type);
  const reduceMotion = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  const [pvCycle, setPvCycle] = useState(0);
  useEffect(() => {
    if (!anim.enabled || tab !== 'anim') return;
    const ms = Math.max(1600, anim.duration * 1000 + 1100);
    const t2 = setInterval(() => setPvCycle((c) => c + 1), ms);
    return () => clearInterval(t2);
  }, [anim.enabled, anim.duration, anim.type, anim.direction, tab]);

  if (!openDialog.layerSettings) return null;

  return (
    <>
      <div className={`pp-cb-scrim ${show ? 'show' : ''}`} onClick={close}></div>
      <div className={`pp-cb-sheet ${show ? 'show' : ''}`} role="dialog" aria-label={title}>
        <div className="pp-cb-grabber"></div>
        <h3>{title}</h3>
        <div className="pp-cb-sheet-sub">{t('show.show_layer_sheet_sub')}</div>

        <div className="pp-cb-tabs">
          <button className={`pp-cb-tab ${tab === 'display' ? 'on' : ''}`} onClick={() => setTab('display')}>{t('show.show_tab_display')}</button>
          <button className={`pp-cb-tab ${tab === 'anim' ? 'on' : ''}`} onClick={() => setTab('anim')}>{t('show.show_tab_transition')}</button>
        </div>

        <div className="pp-cb-sheet-body">
          {tab === 'display' ? (
            <>
              <div className="pp-cb-master">
                <div className="sl">
                  <div className="a">{t('show.show_unify_master_title')}</div>
                  <div className="b">{t('show.show_unify_master_desc')}</div>
                  {d.apply_layer_settings && hasVideo && (
                    <div className="w">{t('show.show_unify_video_warning')}</div>
                  )}
                </div>
                <button
                  type="button"
                  className={`pp-cb-sw ${d.apply_layer_settings ? 'on' : ''}`}
                  role="switch"
                  aria-checked={d.apply_layer_settings}
                  onClick={() => setD((p) => ({ ...p, apply_layer_settings: !p.apply_layer_settings }))}
                />
              </div>

              {!d.apply_layer_settings && (
                <div className="pp-cb-inote">{t('show.show_unify_off_note')}</div>
              )}

              <div className={d.apply_layer_settings ? '' : 'pp-cb-dim'}>
                <div className="pp-cb-stitle">{t('show.show_media_settings_title')}</div>
                <div className="pp-cb-sgroup">
                  <div className="pp-cb-srow">
                    <div className="sl"><div className="a">{t('dragdrop.mute')}</div></div>
                    <button
                      type="button"
                      className={`pp-cb-sw ${d.is_muted ? 'on' : ''}`}
                      role="switch"
                      aria-checked={d.is_muted}
                      onClick={() => setD((p) => ({ ...p, is_muted: !p.is_muted }))}
                    />
                  </div>
                  <div className="pp-cb-fitbox">
                    <div className="a">{t('dragdrop.image_fit.label')}</div>
                    <div className="pp-cb-seg wide">
                      {FITS.map((mode) => (
                        <button key={mode} type="button" className={d.image_fit === mode ? 'on' : ''}
                          onClick={() => setD((p) => ({ ...p, image_fit: mode }))}>
                          {t(`dragdrop.image_fit.${mode}`)}
                        </button>
                      ))}
                    </div>
                    <div className="pp-cb-fit-hint">{t(`show.show_fit_hint_${d.image_fit}`)}</div>
                  </div>
                </div>

                {layer === 'MAIN' && (
                  <>
                    <div className="pp-cb-stitle">{t('show.show_layer_overlays_title')}</div>
                    <div className="pp-cb-sgroup">
                      <div className="pp-cb-srow">
                        <div className="sl"><div className="a">{t('show.show_show_playground_layer')}</div></div>
                        <button type="button" className={`pp-cb-sw ${d.show_layer_one ? 'on' : ''}`} role="switch" aria-checked={d.show_layer_one}
                          onClick={() => setD((p) => ({ ...p, show_layer_one: !p.show_layer_one }))} />
                      </div>
                      <div className="pp-cb-srow">
                        <div className="sl"><div className="a">{t('show.show_show_corner_layer')}</div></div>
                        <button type="button" className={`pp-cb-sw ${d.show_layer_two ? 'on' : ''}`} role="switch" aria-checked={d.show_layer_two}
                          onClick={() => setD((p) => ({ ...p, show_layer_two: !p.show_layer_two }))} />
                      </div>
                      <div className="pp-cb-srow">
                        <div className="sl"><div className="a">{t('show.show_show_banner_layer')}</div></div>
                        <button type="button" className={`pp-cb-sw ${d.show_layer_three ? 'on' : ''}`} role="switch" aria-checked={d.show_layer_three}
                          onClick={() => setD((p) => ({ ...p, show_layer_three: !p.show_layer_three }))} />
                      </div>
                    </div>
                  </>
                )}

                <details className="pp-cb-adv">
                  <summary>{t('show.show_advanced_geometry')}</summary>
                  <div className="pp-cb-geo">
                    {[['width', 'show_geo_width'], ['height', 'show_geo_height'], ['start_margin', 'show_geo_start_margin'], ['top_margin', 'show_geo_top_margin']].map(([key, labelKey]) => (
                      <label key={key}>
                        {t(`show.${labelKey}`)}
                        <input
                          type="number"
                          value={d.settings[key]}
                          onChange={(e) => setD((p) => ({ ...p, settings: { ...p.settings, [key]: Number(e.target.value) } }))}
                        />
                      </label>
                    ))}
                  </div>
                </details>
              </div>

              <div style={{ marginTop: 14 }}>
                <Button label={t('buttons.save')} className="w-100" onClick={saveDisplay} loading={updateLayerSettings.isLoading} />
              </div>
            </>
          ) : (
            <>
              <div className="pp-cb-master">
                <div className="sl">
                  <div className="a">{t('show.show_enable_transition')}</div>
                  <div className="b">{t('show.show_enable_transition_desc')}</div>
                </div>
                <button type="button" className={`pp-cb-sw ${anim.enabled ? 'on' : ''}`} role="switch" aria-checked={anim.enabled}
                  onClick={() => setAnim((p) => ({ ...p, enabled: !p.enabled }))} />
              </div>

              <div className="pp-cb-pv">
                <div className={`pane p1 ${pvCycle % 2 === 0 ? 'front' : 'back'}`}>١</div>
                <div className={`pane p2 ${pvCycle % 2 === 1 ? 'front' : 'back'}`}>٢</div>
                <span className="pcap">
                  {anim.enabled
                    ? t('show.show_preview_label', { type: t(`show.show_anim_type_${anim.type}`) })
                    : t('show.show_preview_none')}
                </span>
              </div>

              <div className={anim.enabled ? '' : 'pp-cb-dim'}>
                <div className="pp-cb-stitle">{t('show.show_transition_type')}</div>
                <div className="pp-cb-types">
                  {ANIM_TYPES.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`pp-cb-type ${anim.type === opt.value ? 'on' : ''}`}
                      onClick={() => setAnim((p) => sanitizeAnim({ ...p, type: opt.value }))}
                    >
                      {t(`show.show_anim_type_${opt.value}`)}
                    </button>
                  ))}
                </div>

                {dirOptions.length > 0 && (
                  <>
                    <div className="pp-cb-stitle">{t('show.show_direction')}</div>
                    <div className="pp-cb-seg wide" style={{ marginBottom: 14 }}>
                      {dirOptions.map((dir) => (
                        <button key={dir} type="button" className={anim.direction === dir ? 'on' : ''}
                          onClick={() => setAnim((p) => ({ ...p, direction: dir }))}>
                          {t(`show.show_dir_${dir}`)}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                <div className="pp-cb-stitle">{t('show.show_transition_duration')}</div>
                <div className="pp-cb-rng">
                  <input
                    type="range" min="0.2" max="2" step="0.1"
                    value={anim.duration}
                    onChange={(e) => setAnim((p) => ({ ...p, duration: Number(e.target.value) }))}
                  />
                  <span className="val num">{anim.duration.toFixed(1)} {t('show.show_seconds_unit')}</span>
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <Button label={t('buttons.save')} className="w-100" onClick={saveAnim} loading={animMutation.isLoading} />
              </div>
            </>
          )}
        </div>

        <Button label={t('buttons.cancel')} outlined className="w-100" style={{ marginTop: 10, flex: '0 0 auto' }} onClick={close} />
      </div>
    </>
  );
}

export default BuilderLayerSettingsSheet;
